// FILE: src/utils/sanitizeHtml.js
// Utility helpers for safely rendering user-provided HTML snippets on the client.
// The implementation purposely avoids external dependencies so it can run in
// restricted environments while still neutralising the most common XSS vectors.

const DEFAULT_ALLOWED_TAGS = [
  "a",
  "abbr",
  "b",
  "blockquote",
  "br",
  "code",
  "del",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "img",
  "li",
  "ol",
  "p",
  "pre",
  "s",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];

const DEFAULT_ALLOWED_ATTRIBUTES = {
  "*": ["class", "aria-label", "aria-hidden"],
  a: ["href", "title", "target", "rel"],
  img: ["src", "alt", "title", "width", "height"],
  table: ["role"],
};

const SAFE_URL_PROTOCOLS = ["http:", "https:", "mailto:", "tel:", ""]; // empty => relative
const SAFE_DATA_URL_PREFIXES = [
  "data:image/png",
  "data:image/jpeg",
  "data:image/gif",
  "data:image/webp",
];

function escapeHtml(raw) {
  return String(raw || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function cloneAndNormaliseAttrMap(map) {
  const out = {};
  Object.entries(map || {}).forEach(([tag, attrs]) => {
    out[tag.toLowerCase()] = Array.from(
      new Set((attrs || []).map((attr) => attr.toLowerCase()))
    );
  });
  return out;
}

function mergeAllowedAttributes(base, extra) {
  const result = cloneAndNormaliseAttrMap(base);
  if (!extra) return result;
  Object.entries(extra).forEach(([tag, attrs]) => {
    const key = tag.toLowerCase();
    const existing = result[key] || [];
    const merged = new Set(existing);
    (attrs || []).forEach((attr) => merged.add(attr.toLowerCase()));
    result[key] = Array.from(merged);
  });
  return result;
}

function isSafeUrl(value) {
  const trimmed = value.trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("data:")) {
    return SAFE_DATA_URL_PREFIXES.some((prefix) => lower.startsWith(prefix));
  }
  const protocolMatch = lower.match(/^([a-z0-9+.-]+:)/);
  if (!protocolMatch) return true; // relative URL
  return SAFE_URL_PROTOCOLS.includes(protocolMatch[1]);
}

function removeNodePreserveChildren(parent, node) {
  if (!parent) return;
  const fragment = parent.ownerDocument.createDocumentFragment();
  while (node.firstChild) {
    fragment.appendChild(node.firstChild);
  }
  parent.replaceChild(fragment, node);
}

function cleanTree(root, allowedTags, allowedAttributes) {
  const doc = root.ownerDocument;
  const tagSet = new Set(allowedTags.map((tag) => tag.toLowerCase()));
  const globalAttrs = new Set((allowedAttributes["*"] || []).map((attr) => attr.toLowerCase()));

  function getAllowedAttrSet(tagName) {
    return new Set([...(allowedAttributes[tagName] || []), ...globalAttrs]);
  }

  function traverse(parent, node) {
    if (node.nodeType === Node.COMMENT_NODE) {
      parent.removeChild(node);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const tagName = node.tagName.toLowerCase();
    if (!tagSet.has(tagName)) {
      removeNodePreserveChildren(parent, node);
      return;
    }

    const allowedForTag = getAllowedAttrSet(tagName);
    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value || "";

      if (name.startsWith("on")) {
        node.removeAttribute(attr.name);
        return;
      }

      if (name === "style") {
        node.removeAttribute(attr.name);
        return;
      }

      if (name === "src" || name === "href") {
        if (!isSafeUrl(value)) {
          node.removeAttribute(attr.name);
          return;
        }
      }

      if (!allowedForTag.has(name) && !name.startsWith("data-")) {
        node.removeAttribute(attr.name);
        return;
      }

      if (tagName === "a" && name === "target" && value === "_blank") {
        if (!node.hasAttribute("rel")) {
          node.setAttribute("rel", "noopener noreferrer");
        }
      }
    });

    Array.from(node.childNodes).forEach((child) => traverse(node, child));
  }

  Array.from(root.childNodes).forEach((child) => traverse(root, child));

  // Ensure stray nodes appended to document fragment remain attached
  return doc;
}

export function sanitizeRichText(input, options = {}) {
  const html = typeof input === "string" ? input : "";
  if (!html) return "";

  const allowedTags = (options.allowedTags || DEFAULT_ALLOWED_TAGS).map((tag) =>
    tag.toLowerCase()
  );
  const allowedAttributes = mergeAllowedAttributes(
    DEFAULT_ALLOWED_ATTRIBUTES,
    options.allowedAttributes
  );

  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return escapeHtml(html);
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body = doc.body || doc.createElement("body");
  cleanTree(body, allowedTags, allowedAttributes);
  return body.innerHTML;
}

export function sanitizePlainText(text) {
  return escapeHtml(text).replace(/\r?\n/g, "<br />");
}

export default sanitizeRichText;
